// src/components/admin/AdminTransactions.jsx
import * as React from 'react';
import {
  Paper, Typography, Stack, IconButton, CircularProgress, Alert, Button,
  List, ListItemButton, Chip, Tooltip, TextField, InputAdornment,
  ToggleButtonGroup, ToggleButton, Divider, Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar, Checkbox, FormControlLabel, MenuItem, Select, FormControl, InputLabel, FormHelperText,
  Collapse, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import RestoreIcon from '@mui/icons-material/Restore';
import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { fillTemplate } from '../../lib/template.mailer';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import transactionsI18n from '../../i18n/components/admin/transactions.i18n';
import pricelistI18n from '../../i18n/components/tarifs/pricelist.i18n';
import recapI18n from '../../i18n/pages/recappage.i18n';

const BLUE = '#0466c8';
const BLUE_BG = 'rgba(11,95,255,0.06)';
const BORDER = '#e6efe8';
const HOVER_BG = 'rgba(11,95,255,0.03)';

const money = (n) => Number(n || 0).toFixed(2);

// --- Recap label helpers (from RecapPage)
const strip = (s = '') => String(s).replace(/\s+/g, ' ').trim();

function normalizeComponentPrefix(raw = '', t) {
  // ✅ compat vieux navigateurs : pas de \p{Diacritic}
  let base = '';
  try {
    base = String(raw)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  } catch {
    base = String(raw).toLowerCase().trim();
  }

  const fallback = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

  if (!base) return t('recap.labels.screen', 'Écran');

  // ✅ Écran
  if (base.includes('ecran') || base.includes('screen') || base.includes('lcd') || base.includes('display')) {
    return t('recap.labels.screen', 'Écran');
  }

  // ✅ Carte mère
  // ✅ Carte mère
  if (
    base.includes('carte mere') ||
    base.includes('motherboard') ||
    base.includes('mainboard') ||
    base.includes('logic board')
  ) {
    return t('recap.labels.motherboard', 'Carte mère');
  }

  // ✅ Batterie
  if (base.includes('batterie') || base.includes('battery')) {
    return t('recap.labels.battery', 'Batterie');
  }

  // ✅ Caméra
  if (base.includes('camera') || base.includes('camera module') || base.includes('cam')) {
    return t('recap.labels.camera', 'Caméra');
  }

  // ✅ Haut-parleur
  if ((base.includes('haut') && base.includes('parleur')) || base.includes('speaker') || base.includes('loudspeaker')) {
    return t('recap.labels.speaker', 'Haut-parleur');
  }

  // ✅ Micro
  if (base.includes('microphone') || base.includes('micro')) {
    return t('recap.labels.microphone', 'Micro');
  }

  // ✅ Connecteur de charge
  if (base.includes('connecteur') || base.includes('charge') || base.includes('charging') || base.includes('dock') || base.includes('usb')) {
    return t('recap.labels.chargingConnector', 'Connecteur de charge');
  }

  // ✅ Bouton
  if (base.includes('bouton') || base.includes('button')) {
    return t('recap.labels.button', 'Bouton');
  }

  // ✅ Vibreur
  if (base.includes('vibreur') || base.includes('taptic') || base.includes('vibrator')) {
    return t('recap.labels.vibrator', 'Vibreur');
  }

  return fallback(strip(raw));
}

  function formatRecapLabel(line, t) {
    const id = String(line?.id || '');
    const raw = String(line?.label || '');
    const parts = raw.includes('—') ? raw.split('—').map(strip) : [strip(raw)];
    const screenLabel = t('recap.labels.screen', 'Écran');

    // 🟣 Normalisation du libellé pour le lot "Envoie, on trie pour toi !"
    const low = raw.toLowerCase();
    const isBatchSort =
      low.includes('vendez en 1 clic') ||
      low.includes('wyślij, a my posortujemy') ||
      low.includes('wyslij, a my posortujemy');

    if (isBatchSort || id.startsWith('brand:')) {
      return t('tarifs.pricelist.batchSort.title', 'Envoie, on trie pour toi !');
    }

    // ❌ Ne PAS préfixer le batch-sort
    if (!raw.includes('—')) {
      const lowRaw = raw.toLowerCase();
      const isBatch =
        lowRaw.includes('envoie') ||
        lowRaw.includes('trie pour toi') ||
        lowRaw.includes('vendez en 1 clic') ||
        lowRaw.includes('wyslij') ||
        lowRaw.includes('posortujemy');

      if (isBatch) {
        return t('tarifs.pricelist.batchSort.title', 'Envoie, on trie pour toi !');
      }

      const left = strip(raw);
      return left ? `${screenLabel} — ${left}` : screenLabel;
    }

    // Cas 2 : "Apple IPHONE 13 — Appareil complet" => Écran — Apple IPHONE 13
    if (raw.match(/—\s*Appareil complet\s*$/i)) {
      const left = strip(raw.replace(/—\s*Appareil complet\s*$/i, ''));
      return left ? `${screenLabel} — ${left}` : screenLabel;
    }

    // ✅ model:* => préfix écran
    if (id.startsWith('model:')) {
      const left = parts[0] || raw.replace(/—\s*Appareil complet$/i, '').trim();
      return `${screenLabel} — ${left}`;
    }

    // ✅ component:* => préfix composant + modèle
    if (id.startsWith('component:')) {
      if (parts.length >= 2) {
        const leftBrandModel = parts[0];
        const rightComponent = parts.slice(1).join(' — ');
        const prefix = normalizeComponentPrefix(rightComponent, t);
        return `${prefix} — ${leftBrandModel}`;
      }
      return raw;
    }

    // ✅ FALLBACK "component" même si l'id n'a pas "component:"
    // Ex: "Apple IPHONE 13 — connecteur charge"
    if (parts.length >= 2) {
      const leftBrandModel = parts[0];
      const rightComponent = parts.slice(1).join(' — ');
      const prefix = normalizeComponentPrefix(rightComponent, t);
      return `${prefix} — ${leftBrandModel}`;
    }

    return raw;
  }

// ✅ Same expertise note rendering as AccountTransactions (detail table)
function appendExpertiseNote(it, t, baseLabel) {
  const note = it?.expertiseNote;
  if (note == null) return baseLabel;

  const packagesLabel = t('recap.labels.packages', 'colis');
  const piecesLabel = t('recap.labels.pieces', 'pièces');
  const kgLabel = 'kg';

  // Object note (preferred)
  if (typeof note === 'object') {
    const packagesRaw =
      note.parcelsCount ??
      note.parcels ??
      note.packagesCount ??
      note.packages ??
      note.colis;

    const weightRaw = note.weightKg ?? note.weight;
    const piecesRaw = note.piecesCount ?? note.pieces;

    const piecesStr =
      piecesRaw != null && String(piecesRaw).trim() !== ''
        ? `${String(piecesRaw).trim()} ${piecesLabel}`
        : '';

    const weightStr =
      weightRaw != null && String(weightRaw).trim() !== ''
        ? `${String(weightRaw).trim()} ${kgLabel}`
        : '';

    const packagesStr =
      packagesRaw != null && String(packagesRaw).trim() !== ''
        ? `${String(packagesRaw).trim()} ${packagesLabel}`
        : '';

    const parts = [piecesStr, weightStr, packagesStr].filter(Boolean);
    if (parts.length > 0) return `${baseLabel} (${parts.join(' · ')})`;

    return baseLabel;
  }

  // String note fallback
  const trimmed = String(note).trim();
  if (trimmed) return `${baseLabel} (${trimmed})`;

  return baseLabel;
}

function formatItemLabel(it, t) {
  const baseLabel = formatRecapLabel({ id: it?.id, label: it?.label }, t);
  return appendExpertiseNote(it, t, baseLabel);
}

// ---- Status chip
function StatusChip({ label, on, onClick, disabled, color = 'success', fullWidth = false, forceBlueWhenDisabled = false }) {
  const icon = on ? <CheckCircleOutlineIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />;
  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      onClick={onClick}
      disabled={disabled || !onClick}
      variant={on ? 'filled' : 'outlined'}
      color="default"
      sx={{
        width: fullWidth ? '100%' : undefined,
        justifyContent: 'flex-start',
        borderRadius: 1.25,
        fontWeight: 600,
        px: 1,
        '& .MuiChip-icon': { ml: 0.5 },
        ...(on && {
          bgcolor: BLUE_BG,
          color: BLUE,
          borderColor: BLUE,
        }),
        ...(forceBlueWhenDisabled && {
          '&.Mui-disabled': {
            opacity: 1,
            bgcolor: BLUE_BG,
            color: BLUE,
            borderColor: BLUE,
          },
        }),
      }}
    />
  );
}


const getSriFromLocalStorage = (txId) => {
  try {
    const raw = localStorage.getItem(`tx_sri_${txId}`);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

// Retention helper: cancelled (isActive=false) transactions are deleted after 45 days.
// We compute the target deletion date + remaining days.
const RETENTION_DAYS = 45;
function computeRetentionInfo(tx) {
  // Prefer explicit timestamps if present, fallback to updatedAt/createdAt
  const base =
    tx?.cancelledAt ||
    tx?.canceledAt ||
    tx?.inactiveAt ||
    tx?.inactivatedAt ||
    tx?.updatedAt ||
    tx?.createdAt;

  const baseDate = base ? new Date(base) : null;
  if (!baseDate || Number.isNaN(baseDate.getTime())) return null;

  const deleteAt = new Date(baseDate.getTime() + RETENTION_DAYS * 24 * 60 * 60 * 1000);

  // Remaining days: ceil so "today" shows as 45 days left right after cancel.
  const now = new Date();
  const msLeft = deleteAt.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));

  return { baseDate, deleteAt, daysLeft };
}

export default function AdminTransactions() {
  const nav = useNavigate();

  // i18n
const { t, register } = useI18n();
  React.useEffect(() => {
    register('admin', transactionsI18n);
    register('tarifs', pricelistI18n); // pour accéder à tarifs.pricelist.*
    register('recap', recapI18n);      // ✅ pour recap.labels.* (même rendu que AccountTransactions / RecapPage)
  }, [register]);

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [okMsg, setOkMsg] = React.useState(''); // ✅ message de succès
  const [rows, setRows] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('ongoing'); // all|ongoing|completed|cancelled|waybill|reception|control|pdf|paid
  const [scopeFilter, setScopeFilter] = React.useState('active'); // active | deleted | all

  const [openIds, setOpenIds] = React.useState(() => new Set());

  const toggleDetails = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // PDF contrôle (aperçu)
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState('');
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [pdfErr, setPdfErr] = React.useState('');

  // PDF bordereau (aperçu simple)
  const [wbOpen, setWbOpen] = React.useState(false);
  const [wbBlobUrl, setWbBlobUrl] = React.useState('');
  const [wbLoading, setWbLoading] = React.useState(false);
  const [wbErr, setWbErr] = React.useState('');
  const [wbFor, setWbFor] = React.useState(null);
  const [wbTrackingList, setWbTrackingList] = React.useState([]);

  // Upload
  const [uploadBusyId, setUploadBusyId] = React.useState('');
  const fileInputsRef = React.useRef({}); // id -> input

  // IBAN (admin)
  const [ibanOpen, setIbanOpen] = React.useState(false);
  const [ibanLoading, setIbanLoading] = React.useState(false);
  const [ibanErr, setIbanErr] = React.useState('');
  const [ibanData, setIbanData] = React.useState(null);
  const [revealBusy, setRevealBusy] = React.useState(false);
  const [revealPlain, setRevealPlain] = React.useState(null);
  const [ibanDialogOpen, setIbanDialogOpen] = React.useState(false);

  const [wbSetupOpen, setWbSetupOpen] = React.useState(false);
  const [wbSetupFor, setWbSetupFor] = React.useState(null); // transaction
  const [wbSetupBlobUrl, setWbSetupBlobUrl] = React.useState('');
  const [wbSetupErr, setWbSetupErr] = React.useState('');
  const [trackingInputs, setTrackingInputs] = React.useState(['']);
  const hasAtLeastOneTracking = trackingInputs.some(
    (v) => String(v || '').trim() !== ''
  );
  const [trackingSaving, setTrackingSaving] = React.useState(false);
  const [shippingMethod, setShippingMethod] = React.useState('pickup');
  const [autoSendShippingEmail, setAutoSendShippingEmail] = React.useState(true);

  // 💰 montant de la livraison saisi dans le setup bordereau
  const [shippingAmountInput, setShippingAmountInput] = React.useState('');
  // Retour colis (après refus)
  const [returnOpen, setReturnOpen] = React.useState(false);
  const [returnFor, setReturnFor] = React.useState(null);
  const [returnTracking, setReturnTracking] = React.useState('');
  const [returnAmountInput, setReturnAmountInput] = React.useState('');
  const [returnSaving, setReturnSaving] = React.useState(false);

  const openReturnDialog = (tx) => {
    if (!tx) return;
    setReturnFor(tx);
    setReturnTracking(tx.returnTrackingNumber || '');
    setReturnAmountInput(
      tx && tx.returnShippingAmount != null
        ? String(tx.returnShippingAmount)
        : ''
    );
    setReturnOpen(true);
  };

  const closeReturnDialog = () => {
    setReturnOpen(false);
    setReturnFor(null);
    setReturnTracking('');
    setReturnAmountInput('');
  };

  const saveReturnShipping = async () => {
    if (!returnFor?.id) return;

    const tracking = String(returnTracking || '').trim();
    if (!tracking) {
      setErr('Numéro de suivi retour requis.');
      return;
    }

    const rawAmount = String(returnAmountInput || '').replace(',', '.').trim();
    let amountPayload = undefined;
    if (rawAmount !== '') {
      const n = Number(rawAmount);
      if (Number.isNaN(n) || n < 0) {
        setErr('Montant de livraison retour invalide.');
        return;
      }
      amountPayload = n;
    }

    setReturnSaving(true);
    setErr('');
    setOkMsg('');

    try {
      const payload = {
        trackingNumber: tracking,
        statusReturn: true,        // ✅ on marque que le retour est fait
      };
      if (amountPayload !== undefined) {
        payload.shippingAmount = amountPayload;  // accepté par le backend maintenant
      }

      const { data } = await api.patch(`/transactions/${returnFor.id}/return-shipping`, payload);
      setRows((prev) => prev.map((x) => (x.id === returnFor.id ? data : x)));

      closeReturnDialog();
      setOkMsg('✓ Retour préparé.');
      setTimeout(() => setOkMsg(''), 4000);
    } catch (e) {
      const msg = e?.response?.data?.message || 'Erreur lors de l’enregistrement du retour.';
      setErr(msg);
    } finally {
      setReturnSaving(false);
    }
  };

  // 🔶 Annulation / réactivation transaction
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelFor, setCancelFor] = React.useState(null);
  const [cancelMsg, setCancelMsg] = React.useState('');
  const [cancelSaving, setCancelSaving] = React.useState(false);

  const openCancelDialog = (tx) => {
    if (!tx) return;
    setCancelFor(tx);
    setCancelMsg(tx.inactiveComment || '');
    setCancelOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelOpen(false);
    setCancelFor(null);
    setCancelMsg('');
  };

  const saveCancel = async () => {
    if (!cancelFor?.id) return;

    setCancelSaving(true);
    setErr('');
    setOkMsg('');
    try {
      const payload = {
        active: false,
        inactiveComment: cancelMsg || null,
      };
      const { data } = await api.patch(`/transactions/${cancelFor.id}/cancel`, payload);
      setRows((prev) => prev.map((x) => (x.id === cancelFor.id ? data : x)));

      setOkMsg('✓ Transaction annulée.');
      setTimeout(() => setOkMsg(''), 4000);
      closeCancelDialog();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Erreur lors de l’annulation de la transaction.';
      setErr(msg);
    } finally {
      setCancelSaving(false);
    }
  };

  const reactivate = async (tx) => {
    if (!tx?.id) return;
    setErr('');
    setOkMsg('');
    try {
      const payload = {
        active: true,
        inactiveComment: tx.inactiveComment || null,
      };
      const { data } = await api.patch(`/transactions/${tx.id}/cancel`, payload);
      setRows((prev) => prev.map((x) => (x.id === tx.id ? data : x)));
      setOkMsg('✓ Transaction réactivée.');
      setTimeout(() => setOkMsg(''), 4000);
    } catch (e) {
      const msg = e?.response?.data?.message || 'Erreur lors de la réactivation de la transaction.';
      setErr(msg);
    }
  };

  const [contactUser, setContactUser] = React.useState(null);


  const openContactUser = (user) => {
    setContactUser(user || null);
  };

  const closeContactUser = () => {
    setContactUser(null);
  };
  // ✉️ Composer e-mail (multilingue)
  const [mailOpen, setMailOpen] = React.useState(false);
  const [mailFor, setMailFor] = React.useState(null);
  const [mailTo, setMailTo] = React.useState('');
  const [mailSubject, setMailSubject] = React.useState('');
  const [mailBody, setMailBody] = React.useState('');
  const [mailAttachWaybill, setMailAttachWaybill] = React.useState(true);
  const [mailAttachRecap, setMailAttachRecap] = React.useState(false);
  const [mailSending, setMailSending] = React.useState(false);
  const [mailLang, setMailLang] = React.useState('fr');
  const [mailScenario, setMailScenario] = React.useState('waybill'); // 'waybill' | 'reception' | 'control' | 'payment'

  // ✨ Décision offre (admin)
  const [decisionDialogOpen, setDecisionDialogOpen] = React.useState(false);
  const [decisionTx, setDecisionTx] = React.useState(null);
  const [decisionLoading, setDecisionLoading] = React.useState(false);
  const [decisionErr, setDecisionErr] = React.useState('');
  const [rejectWarningOpen, setRejectWarningOpen] = React.useState(false);

  // Petit popup pour choisir le type d’e-mail
  const [mailTypeOpen, setMailTypeOpen] = React.useState(false);
  const [mailTypeTx, setMailTypeTx] = React.useState(null);

  const computeWaybillLink = (txId) => `/transactions/${txId}/waybill.pdf`;
  const computeRecapLink   = (txId) => `/transactions/${txId}/verification.pdf`;

  const applyTemplate = (scenario, lang, tx, { forceReplace = false } = {}) => {
    if (!tx) return;

    const piecesCount = tx.quantity_received != null
      ? Number(tx.quantity_received)
      : null;

    const brut = Number(
      tx.totalAmount ??
      tx.total ??
      tx.finalAmount ??
      tx.priceEstimate ??
      0
    );
    const shippingMain   = Number(tx.shippingAmount || 0);
    const shippingReturn = Number(tx.returnShippingAmount || 0);
    const recordedPayment = Number(tx.paymentAmount || 0);
    const amountNet = recordedPayment || Math.max(0, Number(tx.totalBuyback || brut) - shippingMain - shippingReturn);

    const trackingCombinedForMail = Array.isArray(tx.shippingTrackingNumbers)
      ? tx.shippingTrackingNumbers.join(' / ')
      : (tx.shippingTrackingNumber || '');

    const vars = {
      numPackage: tx.numPackage,
      linkWaybill: computeWaybillLink(tx.id),
      linkRecap: computeRecapLink(tx.id),
      tracking: trackingCombinedForMail,
      piecesCount,
      amountNet: money(amountNet),
      shippingMethod,
    };

    const { subject, body } = fillTemplate(scenario, lang, vars);
    if (forceReplace || !mailSubject.trim()) setMailSubject(subject);
    if (forceReplace || !mailBody.trim()) setMailBody(body);
  };

  const openMail = (tx, { lang = 'fr', scenario = 'waybill' } = {}) => {
    if (!tx) return;
    const hasWaybill = !!tx.statusWaybillCreated;
    const hasRecap   = !!tx.statusPdfRecap;

    // Pour bordereau / contrôle, on exige le PDF
    if (scenario === 'waybill' && !hasWaybill) {
      setErr("Le bordereau est introuvable pour cette transaction. Téléversez-le d’abord.");
      return;
    }
    if (scenario === 'control' && !hasRecap) {
      setErr("Le récapitulatif est introuvable pour cette transaction. Générez-le d’abord.");
      return;
    }

    setMailFor(tx);
    setMailTo(tx?.user?.email || '');
    setMailLang(lang);
    setMailScenario(scenario);

    setMailAttachWaybill(scenario === 'waybill');
    setMailAttachRecap(scenario === 'control');

    setMailSubject('');
    setMailBody('');

    applyTemplate(scenario, lang, tx, { forceReplace: true });
    setMailOpen(true);
  };

  // ✍️ Composer un e-mail libre (sans modèle, sans PJ forcée)
  const openManualMail = (tx, { lang = 'fr' } = {}) => {
    if (!tx) return;

    setMailFor(tx);
    setMailTo(tx?.user?.email || '');
    setMailLang(lang);

    // On garde un scénario par défaut pour que le bouton "Insérer le modèle" reste utilisable
    setMailScenario('waybill');

    // Pas de PJ par défaut en mode libre
    setMailAttachWaybill(false);
    setMailAttachRecap(false);

    // Champs vides (l'utilisateur écrit)
    setMailSubject('');
    setMailBody('');

    setMailOpen(true);
  };

  const closeMail = () => {
    setMailOpen(false);
    setMailFor(null);
    setMailBody('');
    setMailSubject('');
  };

  const openMailTypeDialog = (tx) => {
    setMailTypeTx(tx || null);
    setMailTypeOpen(true);
  };

  const closeMailTypeDialog = () => {
    setMailTypeOpen(false);
    setMailTypeTx(null);
  };

  const handleMailScenarioSelect = (scenario) => {
    if (!mailTypeTx) return;
    closeMailTypeDialog();
    openMail(mailTypeTx, { lang: 'fr', scenario });
  };

  const copyEmail = (email) => {
    if (!email) return;
    navigator.clipboard?.writeText(email);
  };

  const copyTracking = (tracking) => {
    if (!tracking) return;
    navigator.clipboard?.writeText(tracking);
  };

  const copyNumPackage = (num) => {
    if (!num) return;
    navigator.clipboard?.writeText(String(num));
  };

  // Helper pour obtenir l'URL de suivi et le transporteur
  const getTrackingInfo = (trackingNumber) => {
    if (!trackingNumber) return { url: null, carrier: null };
    const raw = String(trackingNumber).trim();
    if (!raw) return { url: null, carrier: null };

    const upper = raw.toUpperCase();

    // UPS : commence presque toujours par 1Z...
    if (upper.startsWith('1Z')) {
      return {
        carrier: 'ups',
        url: `https://www.ups.com/track?loc=fr_FR&tracknum=${encodeURIComponent(raw)}&requester=ST/trackdetails`,
      };
    }

    // Par défaut : Chronopost
    return {
      carrier: 'chronopost',
      url: `https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT=${encodeURIComponent(raw)}`,
    };
  };

  const fetchRows = async () => {
    setErr(''); setOkMsg(''); setLoading(true);
    try {
      let url = '/transactions';
      if (scopeFilter === 'deleted') url += '?onlyDeleted=true';
      else if (scopeFilter === 'all') url += '?includeDeleted=true';

      const { data } = await api.get(url);
      const list = Array.isArray(data) ? data : [];
      list.sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
      setRows(list);
    } catch (e) {
      setErr(e?.response?.data?.message || t('admin.transactions.errors.load'));
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => { fetchRows(); }, [scopeFilter]);

  React.useEffect(() => {
    // Quand on passe sur « supprimées », on remet le filtre d’étape sur "all"
    // pour éviter des listes vides (ex: supprimées mais pas payées).
    if (scopeFilter === 'deleted' && statusFilter !== 'all') {
      setStatusFilter('all');
    }
  }, [scopeFilter]);

  const patchStatus = async (id, patch) => {
    setErr(''); setOkMsg('');
    try {
      const { data } = await api.patch(`/transactions/${id}`, patch);
      setRows(prev => prev.map(x => (x.id === id ? data : x)));
      return data;
    } catch (e) {
      setErr(e?.response?.data?.message || t('admin.transactions.errors.update'));
    }
  };

  const togglePaymentAndOpenMail = async (tx) => {
    const nextPaid = !tx.statusPayment;
    const updated = await patchStatus(tx.id, { statusPayment: nextPaid });
    if (nextPaid && updated) openMail(updated, { lang: 'fr', scenario: 'payment' });
  };

  // ---- PDFs (lecture simple)
  const openPdf = async (tx) => {
    if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(''); }
    setPdfErr(''); setPdfLoading(true); setPdfOpen(true);
    try {
      const resp = await api.get(`/transactions/${tx.id}/verification.pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (e) { setPdfErr(e?.response?.data?.message || t('admin.transactions.pdf.verif.error')); }
    finally { setPdfLoading(false); }
  };
  const closePdf = () => { setPdfOpen(false); if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(''); setPdfErr(''); };

  const openWaybillPdf = async (tx) => {
    if (wbBlobUrl) { URL.revokeObjectURL(wbBlobUrl); setWbBlobUrl(''); }
    setWbErr('');
    setWbLoading(true);
    setWbOpen(true);

    // Sauvegarde de la transaction et préparation de la liste de numéros de suivi
    setWbFor(tx);

    const list = [];

    // 🚚 Aller : JSON d'abord, puis fallback string éventuel
    if (Array.isArray(tx.shippingTrackingNumbers)) {
      list.push(...tx.shippingTrackingNumbers.filter(Boolean));
    } else if (tx.shippingTrackingNumber) {
      String(tx.shippingTrackingNumber)
        .split(/[\/\n,]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((val) => list.push(val));
    }

    // 🔁 Retour : JSON d'abord, puis fallback string éventuel
    if (Array.isArray(tx.returnTrackingNumbers)) {
      list.push(...tx.returnTrackingNumbers.filter(Boolean));
    } else if (tx.returnTrackingNumber) {
      String(tx.returnTrackingNumber)
        .split(/[\/\n,]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((val) => list.push(val));
    }

    setWbTrackingList(list);

    try {
      const resp = await api.get(`/transactions/${tx.id}/waybill.pdf?ts=${Date.now()}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      setWbBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setWbErr(e?.response?.data?.message || t('admin.transactions.pdf.waybill.error'));
    } finally {
      setWbLoading(false);
    }
  };

  const closeWaybill = () => {
    setWbOpen(false);
    if (wbBlobUrl) URL.revokeObjectURL(wbBlobUrl);
    setWbBlobUrl('');
    setWbErr('');
    setWbFor(null);
    setWbTrackingList([]);
  };

  // 👉 Ouverture “setup bordereau + saisie n°”
  const openWaybillSetup = async (tx, preloadNumber = '') => {
    if (wbSetupBlobUrl) { URL.revokeObjectURL(wbSetupBlobUrl); setWbSetupBlobUrl(''); }
    setWbSetupErr('');
    setWbSetupFor(tx);

    // Si on a déjà une liste JSON, on la prend en priorité
    let initialTrackingList = [];
    if (Array.isArray(tx.shippingTrackingNumbers)) {
      initialTrackingList = tx.shippingTrackingNumbers
        .map((s) => String(s || '').trim())
        .filter(Boolean);
    } else {
      const baseTracking = preloadNumber || tx?.shippingTrackingNumber || '';
      initialTrackingList = String(baseTracking)
        .split(/[\/\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }

    setTrackingInputs(initialTrackingList.length ? initialTrackingList : ['']);

    // 💰 si la transaction a déjà un montant de livraison, on le pré-remplit
    setShippingAmountInput(
      tx && tx.shippingAmount != null
        ? String(tx.shippingAmount)
        : ''
    );

    setWbSetupOpen(true);
    try {
      const resp = await api.get(`/transactions/${tx.id}/waybill.pdf?ts=${Date.now()}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      setWbSetupBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setWbSetupErr(e?.response?.data?.message || t('admin.transactions.pdf.waybill.error'));
    }
  };

  const closeWaybillSetup = () => {
    setWbSetupOpen(false);
    if (wbSetupBlobUrl) URL.revokeObjectURL(wbSetupBlobUrl);
    setWbSetupBlobUrl('');
    setWbSetupFor(null);
    setWbSetupErr('');
    setTrackingInputs(['']);
    setShippingAmountInput('');
  };

  const saveTrackingNumber = async () => {
    if (!wbSetupFor?.id) return;
    const trackingList = trackingInputs
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    if (!trackingList.length) return;

    const tracking = trackingList.join(' / ');

    // 💰 parse montant livraison
    const rawShipping = String(shippingAmountInput || '').replace(',', '.').trim();
    let shippingPayload = undefined;
    if (rawShipping !== '') {
      const n = Number(rawShipping);
      if (Number.isNaN(n) || n < 0) {
        setErr('Montant de livraison invalide.');
        return;
      }
      shippingPayload = n;
    }

    setTrackingSaving(true);
    setErr(''); setOkMsg('');
    try {
      // On envoie à la fois le string legacy et le tableau JSON
      const payload = {
        trackingNumber: tracking,
        trackingNumbers: trackingList,
      };
      if (shippingPayload !== undefined) {
        payload.shippingAmount = shippingPayload;
      }

      const { data } = await api.patch(`/transactions/${wbSetupFor.id}/shipping`, payload);
      setRows(prev => prev.map(x => (x.id === wbSetupFor.id ? data : x)));

      const mailTx = { ...data, shippingTrackingNumber: tracking, shippingTrackingNumbers: trackingList };
      closeWaybillSetup();
      if (autoSendShippingEmail) {
        const generated = fillTemplate('waybill', 'fr', { numPackage: mailTx.numPackage, tracking, shippingMethod });
        await api.post(`/transactions/${mailTx.id}/email`, {
          to: mailTx?.user?.email,
          subject: generated.subject,
          body: generated.body,
          attachWaybill: true,
          attachRecap: false,
        });
        setOkMsg('✓ Suivi enregistré et e-mail d’expédition envoyé.');
      } else {
        openMail(mailTx, { lang: 'fr', scenario: 'waybill' });
      }
    } catch (e) {
      const msg = e?.response?.data?.message || t('admin.transactions.errors.saveTracking');
      setErr(msg);
    } finally {
      setTrackingSaving(false);
    }
  };

  const onChooseFile = async (id, ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    const fileOk = file && file.type === 'application/pdf';
    if (!fileOk) return;

    const fd = new FormData();
    fd.append('file', file);

    setUploadBusyId(id);
    setErr('');
    setOkMsg('');

    try {
      await api.post(`/transactions/${id}/waybill?overwrite=true`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await new Promise((r) => setTimeout(r, 500));

      const { data: updatedTx } = await api.get(`/transactions/${id}`);
      setRows((prev) => prev.map((x) => (x.id === id ? updatedTx : x)));

      await openWaybillSetup(updatedTx, '');
    } catch (e) {
      console.error('Erreur upload bordereau:', e);
      setErr(
        e?.response?.data?.message ||
          t('admin.transactions.errors.uploadWaybill') ||
          'Erreur lors de l’upload du bordereau.'
      );
    } finally {
      setUploadBusyId('');
    }
  };

  // ---- IBAN (admin)
  const openIban = async (tx) => {
    if (!tx?.user?.id) return;
    setIbanOpen(true); setIbanErr(''); setIbanLoading(true);
    setRevealPlain(null);
    try {
      const { data } = await api.get(`/iban/users/${tx.user.id}`);
      setIbanData(data || null);
    } catch (e) {
      setIbanErr(e?.response?.data?.message || t('admin.transactions.iban.error'));
      setIbanData(null);
    } finally {
      setIbanLoading(false);
    }
  };
  const revealIban = async () => {
    if (!ibanData?.userId) return;
    setIbanErr(''); setRevealBusy(true);
    try {
      const { data } = await api.post(`/iban/users/${ibanData.userId}/reveal`, {});
      setRevealPlain({ iban: data?.iban || null, ownerName: data?.ownerName || '' });
      setIbanDialogOpen(true);
    } catch (e) {
      setIbanErr(e?.response?.data?.message || t('admin.transactions.iban.revealError'));
      setRevealPlain(null);
    } finally {
      setRevealBusy(false);
    }
  };
  const closeIban = () => {
    setIbanOpen(false);
    setIbanData(null);
    setRevealPlain(null);
    setIbanErr('');
  };

  React.useEffect(() => () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    if (wbBlobUrl) URL.revokeObjectURL(wbBlobUrl);
    if (wbSetupBlobUrl) URL.revokeObjectURL(wbSetupBlobUrl);
  }, [pdfBlobUrl, wbBlobUrl, wbSetupBlobUrl]);

  // ✅ Filtres incl. "En cours" / "Terminées" + scope
  const filtered = rows.filter(tx => {
    const text = (q || '').trim().toLowerCase();
    if (text) {
      const idShort = String(tx.id || '').slice(0, 8).toLowerCase();
      const email = String(tx?.user?.email || '').toLowerCase();
      const pkg = String(tx?.numPackage || '').toLowerCase();
      if (![idShort, email, pkg].some(v => v.includes(text))) return false;
    }
    // Scope: En cours / Supprimées / Toutes
    if (scopeFilter === 'active' && tx.isDeleted) return false;
    if (scopeFilter === 'deleted' && !tx.isDeleted) return false;

    switch (statusFilter) {
      case 'ongoing':   // ventes en cours → pas encore payées, et non annulées
        return !tx.statusPayment && tx.isActive !== false;
      case 'completed': // ventes terminées → payées
        return !!tx.statusPayment;
      case 'cancelled': // transactions annulées (non supprimées)
        return tx.isActive === false && !tx.isDeleted;
      case 'waybill': return !!tx.statusWaybillCreated;
      case 'reception': return !!tx.statusReception;
      case 'control': return !!tx.statusControlQuality;
      case 'pdf': return !!tx.statusPdfRecap;
      case 'paid': return !!tx.statusPayment;
      default: return true;
    }
  });

  // Toolbar
  const Toolbar = () => (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar sx={{ bgcolor: BLUE, width: 28, height: 28, fontSize: 14, fontWeight: 800 }}>TX</Avatar>
        <Typography variant="h6" sx={{ fontWeight: 800, color: BLUE }}>
          {t('admin.transactions.title')}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
        <TextField
          size="small"
          placeholder={t('admin.transactions.search.placeholder')}
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          sx={{ minWidth: { xs: 0, sm: 240, md: 280 }, flex: { xs: 1, sm: 'unset' } }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scopeFilter}
          onChange={(_, v) => { if (v) setScopeFilter(v); }}
          sx={{
            whiteSpace: 'nowrap',
            '& .MuiToggleButton-root': { textTransform: 'none', borderColor: BORDER, px: 1, py: 0.25 },
            '& .Mui-selected': { bgcolor: BLUE_BG, color: BLUE },
          }}
        >
          <ToggleButton value="active">En cours</ToggleButton>
          <ToggleButton value="deleted">Supprimées (45j)</ToggleButton>
          <ToggleButton value="all">Toutes</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_,v)=>{ if (v) setStatusFilter(v); }}
          sx={{
            whiteSpace: 'nowrap',
            '& .MuiToggleButton-root': { textTransform: 'none', borderColor: BORDER, px: 1, py: 0.25 },
            '& .Mui-selected': { bgcolor: BLUE_BG, color: BLUE }
          }}
        >
          <ToggleButton value="cancelled">Annulées</ToggleButton>
          <ToggleButton value="all">
            <FilterAltOutlinedIcon sx={{ mr: 0.5 }} fontSize="small" />
            {t('admin.transactions.filters.all')}
          </ToggleButton>
          {/* ✅ Nouveaux raccourcis de tri */}
          <ToggleButton value="ongoing">{t('admin.transactions.filters.ongoing')}</ToggleButton>
          <ToggleButton value="completed">{t('admin.transactions.filters.completed')}</ToggleButton>
          {/* Filtres par étape */}
          <ToggleButton value="waybill">{t('admin.transactions.filters.waybill')}</ToggleButton>
          <ToggleButton value="reception">{t('admin.transactions.filters.reception')}</ToggleButton>
          <ToggleButton value="control">{t('admin.transactions.filters.control')}</ToggleButton>
          <ToggleButton value="pdf">{t('admin.transactions.filters.pdf')}</ToggleButton>
          <ToggleButton value="paid">{t('admin.transactions.filters.paid')}</ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={fetchRows} disabled={loading} sx={{ color: BLUE }} aria-label={t('admin.transactions.actions.refresh')}>
          <RefreshIcon />
        </IconButton>
      </Stack>
    </Stack>
  );

  const sendManualEmail = async () => {
    if (!mailFor?.id) return;

    const hasWaybill = !!mailFor?.statusWaybillCreated;
    const hasRecap   = !!mailFor?.statusPdfRecap;

    if (mailAttachWaybill && !hasWaybill) {
      setErr("Le bordereau est introuvable pour cette transaction. Téléversez-le d’abord.");
      return;
    }
    if (mailAttachRecap && !hasRecap) {
      setErr("Le récapitulatif est introuvable pour cette transaction. Générez-le d’abord.");
      return;
    }

    setMailSending(true);
    setErr(''); setOkMsg('');
    try {
      await api.post(`/transactions/${mailFor.id}/email`, {
        to: (mailTo || mailFor?.user?.email || '').trim(),
        subject: mailSubject,
        body: mailBody,
        attachWaybill: !!mailAttachWaybill,
        attachRecap: !!mailAttachRecap,
      });
      closeMail();
      setOkMsg('✓ E-mail envoyé.');
      setTimeout(() => setOkMsg(''), 4000);
    } catch (e) {
      const apiMsg   = e?.response?.data?.message;
      const smtpMsg  = e?.response?.data?.error?.detail || e?.response?.data?.error?.code;
      setErr(apiMsg || smtpMsg || t('admin.transactions.errors.sendEmail') || 'Erreur lors de l’envoi de l’e-mail');
    } finally {
      setMailSending(false);
    }
  };

  const downloadSimpleExcel = async (tx) => {
    if (!tx?.id) return;
    setErr('');
    try {
      const resp = await api.get(`/transactions/${tx.id}/export-simple.xlsx`, {
        responseType: 'blob',
      });

      const blob = new Blob([resp.data], {
        type:
          resp.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `liste-${tx.numPackage || tx.id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(
        e?.response?.data?.message ||
          'Erreur lors du téléchargement de la liste (Excel).'
      );
    }
  };

  // ===== Décision offre (admin) =====
  const startDecision = (tx) => {
    if (!tx) return;
    setDecisionTx(tx);
    setDecisionErr('');
    setRejectWarningOpen(false);
    setDecisionDialogOpen(true);
  };

  const submitDecision = async (choice) => {
    if (!decisionTx?.id) return;
    setDecisionErr('');
    setDecisionLoading(true);
    try {
      await api.patch(`/transactions/${decisionTx.id}/decision`, { decision: choice });

      // Refresh local row (best-effort)
      setRows((prev) =>
        prev.map((x) => (x.id === decisionTx.id ? { ...x, decision: choice } : x))
      );

      setDecisionDialogOpen(false);
      setRejectWarningOpen(false);
      setDecisionTx(null);

      // Optionnel: refetch pour être sûr d'avoir les champs à jour
    await fetchRows();
    } catch (e) {
      setDecisionErr(
        e?.response?.data?.message ||
          'Erreur lors de la mise à jour de la décision.'
      );
    } finally {
      setDecisionLoading(false);
    }
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: `1px solid ${BORDER}`, background: '#fff' }}>
        <Toolbar />
        <Divider sx={{ mb: 2, borderColor: BORDER }} />

        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {okMsg && <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>}

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18}/><Typography>{t('admin.transactions.loading')}</Typography></Stack>
        ) : filtered.length === 0 ? (
          <Typography sx={{ color: 'text.secondary' }}>{t('admin.transactions.empty')}</Typography>
        ) : (
          <List dense sx={{ m: 0 }}>
            {filtered.map((tx) => {
              const created = new Date(tx.createdAt).toLocaleString();
              // 🔢 Nombre de pièces (affichage)
              // Cas spécial : le lot "Envoie, on trie pour toi !" est souvent qty=1 mais représente un nombre de pièces
              const itemsCount = Array.isArray(tx.items)
                ? tx.items.reduce((sum, it) => {
                    const qty = Number(it?.qty || 0);
                    const rawLabel = String(it?.label || '');
                    const low = rawLabel.toLowerCase();

                    // Détection du lot batch-sort (plusieurs variantes possibles en base)
                    const isBatchSort =
                      low.includes('envoie') && low.includes('trie') ||
                      low.includes('vendez en 1 clic') ||
                      low.includes('wyślij, a my posortujemy') ||
                      low.includes('wyslij, a my posortujemy');

                    if (!isBatchSort) return sum + qty;

                    const expertise = it && typeof it.expertiseNote === 'object' ? it.expertiseNote : null;
                    const pieces = Number(expertise?.piecesCount ?? expertise?.pieces ?? 0);

                    // Si on a un nombre de pièces, on l'affiche à la place de qty (souvent 1)
                    return sum + (pieces > 0 ? pieces : qty);
                  }, 0)
                : 0;

              const rcv = Number(tx.quantity_received || 0);
              const ok  = Number(tx.quantity_verified || 0);
              const ko  = Math.max(0, rcv - ok);

              // 💶 Montants : estimé / rachat / payé / SRI (total du composant vérification)
              const estimatedAmount = Number(tx.priceEstimate || 0);
              const buybackAmount   = Number(tx.totalBuyback || 0);
              const paidAmount      = Number(tx.paymentAmount || 0);

              // 👉 Montants "à payer" / "payé" calculés proprement
              // - Avant paiement : on montre en priorité le rachat, sinon l'estimation
              // - Après paiement : on montre en priorité le montant payé, sinon le rachat
              const amountToPay = buybackAmount || estimatedAmount || 0;
              const amountPaid  = paidAmount || buybackAmount || 0;

              // 🟣 SRI = montant total test issu de la vérification (stocké en localStorage)
              const sriAmount = getSriFromLocalStorage(tx.id);

              const isPaid            = !!tx.statusPayment;
              const mainAmountLabel   = isPaid ? 'Montant payé' : 'Montant à payer';
              const mainAmountValue   = isPaid ? amountPaid : amountToPay;

              const canControl = !!tx.statusReception;
              const canPdf = !!tx.statusControlQuality;
              const canPay = !!tx.statusPdfRecap;
              const isOpen = openIds.has(tx.id);

              const decision = tx.decision || 'pending';
              const isRejected = decision === 'rejected';
              const isAcceptedWithReturn = decision === 'accepted_with_return';
              const decisionLabel =
                decision === 'accepted'
                  ? 'Décision : offre acceptée'
                  : decision === 'accepted_with_return'
                  ? 'Décision : acceptée + retour HS'
                  : decision === 'rejected'
                  ? 'Décision : offre refusée'
                  : 'Décision en attente';

              const prevStepDone = !!tx.statusPdfRecap;
              const isInactive = tx.isActive === false;
              const isDeleted = !!tx.isDeleted;
              const retentionInfo = isInactive && !isDeleted ? computeRetentionInfo(tx) : null;

              const handleControl = () => {
                if (!canControl) return;
                nav(`/admin/transactions/${tx.id}/verify`);
              };

              return (
                <ListItemButton
                  key={tx.id}
                  sx={{
                    borderRadius: 2,
                    mb: 1.1,
                    p: { xs: 1.1, sm: 1.25 },
                    border: `1px solid ${BORDER}`,
                    background: '#fff',
                    '&:hover': { borderColor: '#cfe1ff', background: HOVER_BG },
                    ...(isInactive && {
                      borderColor: '#ffb74d',
                      background: 'rgba(255, 167, 38, 0.06)',
                    }),
                    ...(isDeleted && {
                      borderColor: '#ef9a9a',
                      background: 'rgba(244, 67, 54, 0.06)',
                    }),
                  }}
                >
                  {/* input fichier caché */}
                  <input
                    type="file"
                    accept="application/pdf"
                    style={{ display: 'none' }}
                    ref={el => {
                      if (el) fileInputsRef.current[tx.id] = el;
                    }}
                    onChange={(ev) => onChooseFile(tx.id, ev)}
                  />

                  <Box sx={{ width: '100%' }}>
                    {/* Contenu principal de la carte */}
                    <Stack spacing={1.25}>
                      {/* Infos (toujours visibles, même si la transaction est annulée) */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        sx={{ gap: 1 }}
                      >
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: BLUE_BG,
                              color: BLUE,
                              fontWeight: 800,
                              fontSize: 13,
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                            title={tx.numPackage}
                          >
                            #{tx.numPackage}
                          </Box>

                          {/* 📋 Copier le numéro de transaction (numPackage) */}
                          <Tooltip title="Copier le numéro de transaction">
                            <IconButton
                              size="small"
                              onClick={() => copyNumPackage(tx.numPackage)}
                              sx={{ ml: -0.25 }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          {isDeleted && (
                            <Chip
                              size="small"
                              label={`Supprimée (rétention)${tx.deletedByRetentionAt ? ' • ' + new Date(tx.deletedByRetentionAt).toLocaleDateString() : ''}`}
                              sx={{ bgcolor: 'rgba(244, 67, 54, 0.10)', color: '#c62828', fontWeight: 700 }}
                            />
                          )}

                        {/* 💶 Prix de base (estimation) */}
                        <Chip
                          size="small"
                          label={`Estimation : ${money(estimatedAmount)} €`}
                        />


                          {/* 🔢 Nombre de pièces */}
                          <Chip size="small" label={`${itemsCount} pcs`} />
                          {tx.user?.email && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Tooltip title="Voir la fiche contact">
                                <Chip
                                  size="small"
                                  label={tx.user.email}
                                  sx={{ bgcolor: BLUE_BG, cursor: 'pointer' }}
                                  onClick={() => openContactUser(tx.user)}
                                />
                              </Tooltip>
                              <Tooltip title={t('admin.transactions.actions.copyEmail') || 'Copier e-mail'}>
                                <IconButton size="small" onClick={() => copyEmail(tx.user.email)}>
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                          {/* 💰 frais livraison */}
                          {tx.shippingAmount && Number(tx.shippingAmount) > 0 && (
                            <Chip
                              size="small"
                              label={`Livraison: ${money(Number(tx.shippingAmount))} €`}
                              sx={{ bgcolor: BLUE_BG }}
                            />
                          )}
                        </Stack>
                        <Typography
                          sx={{
                            color: 'text.secondary',
                            ml: { sm: 1 },
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {created}
                        </Typography>
                      </Stack>

                      {/* 💬 Message d’annulation visible */}
                      {isInactive && (
                        <Box
                          sx={{
                            mb: 1,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: '#ffecb3',
                            border: '1px solid #ffb74d',
                            color: '#b26a00',
                            fontSize: 13,
                            fontWeight: 600,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.25,
                          }}
                        >
                          <div>
                            <b>Annulée</b>{tx.inactiveComment ? ` : ${tx.inactiveComment}` : ''}
                          </div>
                          {retentionInfo && (
                            <div style={{ fontWeight: 500 }}>
                              Suppression automatique le <b>{retentionInfo.deleteAt.toLocaleDateString()}</b> ({retentionInfo.daysLeft} jour{retentionInfo.daysLeft > 1 ? 's' : ''} restant{retentionInfo.daysLeft > 1 ? 's' : ''})
                            </div>
                          )}
                        </Box>
                      )}
                      {/* Étapes + détail (plus de flou si la transaction est annulée) */}
                      <Box sx={{}}>
                        {/* Étapes */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, mb: 0.5, color: '#455a64' }}
                          >
                            {t('admin.transactions.steps.title')}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gap: 0.6,
                              gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0,1fr))',
                                sm: 'repeat(3, minmax(0,1fr))',
                                md: 'repeat(5, minmax(0,1fr))',
                              },
                            }}
                          >
                            <StatusChip
                              label={t('admin.transactions.steps.waybill')}
                              on={!!tx.statusWaybillCreated}
                              disabled
                              fullWidth
                              color="success"
                              forceBlueWhenDisabled
                            />
                            <StatusChip
                              label={t('admin.transactions.steps.reception')}
                              on={!!tx.statusReception}
                              onClick={() => patchStatus(tx.id, { statusReception: !tx.statusReception })}
                              fullWidth
                            />
                            <StatusChip
                              label={t('admin.transactions.steps.control')}
                              on={!!tx.statusControlQuality}
                              disabled
                              fullWidth
                            />
                            <StatusChip
                              label={t('admin.transactions.steps.pdf')}
                              on={!!tx.statusPdfRecap}
                              disabled
                              fullWidth
                            />
                            <Chip
                              size="small"
                              icon={
                                decision === 'accepted' ? (
                                  <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#2e7d32' }} />
                                ) : decision === 'accepted_with_return' ? (
                                  <UndoIcon fontSize="small" sx={{ color: '#ed6c02' }} />
                                ) : decision === 'rejected' ? (
                                  <CloseIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                                ) : (
                                  <RadioButtonUncheckedIcon
                                    fontSize="small"
                                    sx={{ color: prevStepDone ? BLUE : '#9e9e9e' }}
                                  />
                                )
                              }
                              label={decisionLabel}
                              variant={decision === 'pending' ? 'outlined' : 'filled'}
                              sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                borderRadius: 1.25,
                                fontWeight: 600,
                                px: 1,
                                '& .MuiChip-icon': { ml: 0.5 },
                                ...(decision === 'accepted' && {
                                  bgcolor: 'rgba(46,125,50,0.08)',
                                  color: '#2e7d32',
                                  borderColor: '#2e7d32',
                                }),
                                ...(decision === 'accepted_with_return' && {
                                  bgcolor: 'rgba(237,108,2,0.08)',
                                  color: '#ed6c02',
                                  borderColor: '#ed6c02',
                                }),
                                ...(decision === 'rejected' && {
                                  bgcolor: 'rgba(211,47,47,0.08)',
                                  color: '#d32f2f',
                                  borderColor: '#d32f2f',
                                }),
                                ...(decision === 'pending' &&
                                  prevStepDone && {
                                    bgcolor: BLUE_BG,
                                    color: BLUE,
                                    borderColor: BLUE,
                                  }),
                              }}
                            />
                            {!isRejected && (
                              <StatusChip
                                label={t('admin.transactions.steps.paid')}
                                on={!!tx.statusPayment}
                                onClick={() => togglePaymentAndOpenMail(tx)}
                                disabled={!canPay}
                                color="success"
                                fullWidth
                              />
                            )}
                          </Box>

                          {/* Montant à payer / Montant payé */}
                          {(buybackAmount > 0 || paidAmount > 0) && (
                            <Typography sx={{ mt: 1, fontWeight: 700 }}>
                              {`${mainAmountLabel} : ${money(mainAmountValue)} €`}
                            </Typography>
                          )}

                          {/* SRI = Total test issu de la vérification */}
                          {sriAmount > 0 && (
                            <Typography sx={{ mt: 0.25, fontWeight: 600 }}>
                              {`SRI : ${money(sriAmount)} €`}
                            </Typography>
                          )}
                        </Box>

                        {/* Détail de la commande EN DESSOUS de la carte (également flouté si annulée) */}
                        <Collapse in={isOpen}>
                          <TableContainer sx={{ mt: 1.5 }}>
                            <Table size="small" sx={{ minWidth: 480 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Article</TableCell>
                                  <TableCell align="right">Qté</TableCell>
                                  <TableCell align="right">PU</TableCell>
                                  <TableCell align="right">Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(tx.items || []).map((it, idx) => {
                                  const pu = Number(it.unitPrice || 0);
                                  const q = Number(it.qty || 0);
                                  const fullLabel = formatItemLabel(it, t);

                                  return (
                                    <TableRow key={`${tx.id}-${idx}`}>
                                      <TableCell
                                        sx={{
                                          maxWidth: 260,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                        title={fullLabel}
                                      >
                                        {fullLabel}
                                      </TableCell>
                                      <TableCell align="right">{q}</TableCell>
                                      <TableCell align="right">{money(pu)}</TableCell>
                                      <TableCell align="right">
                                        <b>{money(pu * q)}</b>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => downloadSimpleExcel(tx)}
                              sx={{
                                borderColor: BORDER,
                                '&:hover': { borderColor: BLUE, backgroundColor: BLUE_BG },
                              }}
                            >
                              Export Excel (Article / Qté)
                            </Button>
                          </Box>
                        </Collapse>
                      </Box>

                      {/* Actions non floutées : upload, bordereau, contrôle, PDF, IBAN, e-mail, détail, annuler / réactiver */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#455a64' }}>
                          {t('admin.transactions.actions.title')}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            columnGap: 0.75,
                            rowGap: 0.75,
                            alignItems: 'center',
                          }}
                        >
                          {/* 1️⃣ Upload en premier */}
                          <Tooltip title={t('admin.transactions.actions.uploadTooltip')}>
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<CloudUploadIcon />}
                                onClick={() => fileInputsRef.current[tx.id]?.click()}
                                disabled={uploadBusyId === tx.id}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  borderColor: BORDER,
                                  color: BLUE,
                                  '&:hover': { borderColor: BLUE, background: BLUE_BG },
                                  '& .MuiButton-startIcon': { mr: 0.5 },
                                }}
                              >
                                {uploadBusyId === tx.id
                                  ? t('admin.transactions.actions.uploading')
                                  : t('admin.transactions.actions.upload')}
                              </Button>
                            </span>
                          </Tooltip>

                          {/* 2️⃣ Voir bordereau */}
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<DescriptionIcon />}
                            onClick={() => openWaybillPdf(tx)}
                            disabled={!tx.statusWaybillCreated}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              color: BLUE,
                              '& .MuiButton-startIcon': { mr: 0.5 },
                            }}
                          >
                            {t('admin.transactions.actions.waybill')}
                          </Button>

                          {/* 3️⃣ Contrôle */}
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<ManageSearchIcon />}
                            disabled={!canControl}
                            onClick={handleControl}
                            disableElevation
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              fontWeight: 700,
                              bgcolor: BLUE,
                              '&:hover': { bgcolor: '#004890' },
                              '& .MuiButton-startIcon': { mr: 0.5 },
                            }}
                          >
                            {t('admin.transactions.actions.control')}
                          </Button>

                          {/* 4️⃣ PDF récap */}
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => openPdf(tx)}
                            disabled={!canPdf}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              color: BLUE,
                              '& .MuiButton-startIcon': { mr: 0.5 },
                            }}
                          >
                            {t('admin.transactions.actions.pdf')}
                          </Button>

                          {/* IBAN */}
                          <Tooltip title={t('admin.transactions.actions.ibanTooltip')}>
                            <span>
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<AccountBalanceIcon />}
                                onClick={() => openIban(tx)}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  color: BLUE,
                                  '& .MuiButton-startIcon': { mr: 0.5 },
                                }}
                              >
                                {t('admin.transactions.actions.iban')}
                              </Button>
                            </span>
                          </Tooltip>

                          {/* 💌 E-mail générique avec choix du modèle */}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<MailOutlineIcon />}
                            onClick={() => openMailTypeDialog(tx)}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              borderColor: BORDER,
                              '&:hover': { borderColor: BLUE },
                            }}
                          >
                            {t('admin.transactions.actions.sendEmail') || 'Envoyer un e-mail'}
                          </Button>

                          <Tooltip title={!tx.statusPdfRecap ? 'Générez le PDF récapitulatif avant de prendre une décision' : ''}>
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={() => startDecision(tx)}
                                disabled={decisionLoading || !tx.statusPdfRecap}
                                disableElevation
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  fontWeight: 800,
                                  bgcolor: BLUE,
                                  '&:hover': { bgcolor: '#004890' },
                                  '& .MuiButton-startIcon': { mr: 0.5 },
                                }}
                              >
                                {tx.decision ? 'Modifier la décision' : 'Décision offre'}
                              </Button>
                            </span>
                          </Tooltip>

                          {isRejected && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<UndoIcon />}
                              onClick={() => openReturnDialog(tx)}
                              sx={{
                                minWidth: 'auto',
                                px: 1,
                                py: 0.25,
                                borderColor: BORDER,
                                '&:hover': { borderColor: BLUE, backgroundColor: BLUE_BG },
                              }}
                            >
                              Retour colis
                            </Button>
                          )}

                          {/* Toggle détails commande */}
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => toggleDetails(tx.id)}
                            endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              color: BLUE,
                              '&:hover': { backgroundColor: BLUE_BG },
                            }}
                          >
                            {'Détail'}
                          </Button>

                          {/* 🔶 Bouton ANNULER / RÉACTIVER (non flouté) aligné à droite */}
                          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            {!isInactive ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => openCancelDialog(tx)}
                                sx={{
                                  borderColor: BORDER,
                                  color: '#004890',
                                  fontWeight: 700,
                                  '&:hover': {
                                    borderColor: '#004890',
                                    backgroundColor: 'rgba(107, 47, 211, 0.08)',
                                  },
                                }}
                              >
                                Annuler
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                sx={{
                                  bgcolor: '#ff9800',
                                  color: '#fff',
                                  fontWeight: 700,
                                  '&:hover': {
                                    bgcolor: '#ffb74d',
                                    color: '#fff',
                                  },
                                }}
                                onClick={() => reactivate(tx)}
                              >
                                Réactiver
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>

                    </Stack>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Modal PDF contrôle */}
      <Dialog open={pdfOpen} onClose={closePdf} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>{t('admin.transactions.pdf.verif.title')}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ width: '100%', height: '80vh', bgcolor: '#f6fbf7' }}>
            {pdfLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 3 }}>
                <CircularProgress size={18} />
                <Typography>{t('admin.transactions.pdf.verif.loading')}</Typography>
              </Stack>
            ) : pdfErr ? (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Alert severity="error">{pdfErr}</Alert>
              </Stack>
            ) : pdfBlobUrl ? (
              <iframe title="verification-pdf" src={pdfBlobUrl} style={{ width: '100%', height: '100%', border: 0 }} />
            ) : (
              <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                <Typography>{t('admin.transactions.pdf.verif.empty')}</Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {pdfBlobUrl && (
            <Button startIcon={<OpenInNewIcon />} href={pdfBlobUrl} target="_blank" rel="noopener noreferrer" sx={{ color: BLUE }}>
              {t('admin.transactions.common.openInTab')}
            </Button>
          )}
          <Button onClick={closePdf}>{t('admin.transactions.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal bordereau simple */}
      <Dialog open={wbOpen} onClose={closeWaybill} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>{t('admin.transactions.pdf.waybill.title')}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.6fr 0.8fr' },
              minHeight: '70vh',
            }}
          >
            {/* PDF à gauche */}
            <Box sx={{ bgcolor: '#f6fbf7', p: 0 }}>
              {wbLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 3 }}>
                  <CircularProgress size={18} />
                  <Typography>{t('admin.transactions.pdf.waybill.loading')}</Typography>
                </Stack>
              ) : wbErr ? (
                <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                  <Alert severity="error">{wbErr}</Alert>
                </Stack>
              ) : wbBlobUrl ? (
                <iframe
                  title="waybill-pdf"
                  src={wbBlobUrl}
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                  <Typography>{t('admin.transactions.pdf.waybill.empty')}</Typography>
                </Stack>
              )}
            </Box>

            {/* Liste des numéros de suivi à droite */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                Numéros de suivi
              </Typography>

              {wbTrackingList.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                  Aucun numéro de suivi enregistré pour ce bordereau.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {wbTrackingList.map((num, idx) => {
                    const { url, carrier } = getTrackingInfo(num);
                    const openLabel =
                      carrier === 'ups'
                        ? 'Ouvrir le suivi UPS'
                        : carrier === 'chronopost'
                        ? 'Ouvrir le suivi Chronopost'
                        : 'Ouvrir le suivi';

                    return (
                      <Stack key={idx} direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          icon={<LocalShippingIcon fontSize="small" />}
                          label={num}
                          sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 700 }}
                        />
                        <Tooltip title="Copier le numéro de suivi">
                          <IconButton size="small" onClick={() => copyTracking(num)}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        {url && (
                          <Tooltip title={openLabel}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              <OpenInNewIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {wbBlobUrl && (
            <Button startIcon={<OpenInNewIcon />} href={wbBlobUrl} target="_blank" rel="noopener noreferrer" sx={{ color: BLUE }}>
              {t('admin.transactions.common.openInTab')}
            </Button>
          )}
          <Button onClick={closeWaybill}>{t('admin.transactions.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* ✨ Modal Setup Bordereau + N° de colis (split) */}
      {/* ✨ Décision offre (admin) */}
      <Dialog
        open={decisionDialogOpen}
        onClose={decisionLoading ? undefined : () => { setRejectWarningOpen(false); setDecisionDialogOpen(false); }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: BLUE }}>
          Décision — offre de rachat
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          {decisionErr && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {decisionErr}
            </Alert>
          )}

          {decisionTx?.decision && decisionTx.decision !== 'pending' && (
            <Alert
              severity="info"
              sx={{
                mb: 1.5,
                bgcolor: BLUE_BG,
                color: BLUE,
                '& .MuiAlert-icon': { color: BLUE },
              }}
            >
              Décision actuelle : <b>{decisionTx.decision}</b> — vous pouvez la modifier.
            </Alert>
          )}

          {decisionTx && (() => {
            const estimated = Number(decisionTx.priceEstimate || 0);
            const buyback = Number(decisionTx.totalBuyback || 0);
            const paidAmount = Number(decisionTx.paymentAmount || 0);
            const validated = buyback || paidAmount || estimated;
            const shippingAmount = Number(decisionTx.shippingAmount || 0);
            const acceptedWithReturnTotal = Math.max(0, validated - shippingAmount);

            return (
              <Stack spacing={1.25}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => submitDecision('accepted')}
                  disabled={decisionLoading}
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{
                    justifyContent: 'flex-start',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                    bgcolor: BLUE,
                    '&:hover': { bgcolor: '#004890' },
                    py: 1.4,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={0.25}>
                    <Typography sx={{ fontWeight: 900 }}>Accepter l’offre</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Le client accepte le rachat.
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 700 }}>
                      Montant: {money(validated)} €
                    </Typography>
                  </Stack>
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setRejectWarningOpen(true)}
                  disabled={decisionLoading}
                  sx={{
                    justifyContent: 'flex-start',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                    py: 1.4,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={0.25}>
                    <Typography sx={{ fontWeight: 900 }}>Refuser l’offre</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Le client refuse le rachat.
                    </Typography>
                  </Stack>
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => submitDecision('accepted_with_return')}
                  disabled={decisionLoading}
                  startIcon={<LocalShippingIcon />}
                  sx={{
                    justifyContent: 'flex-start',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 800,
                    py: 1.4,
                    bgcolor: '#f4f7ff',
                    '&:hover': { bgcolor: '#e7edff' },
                  }}
                >
                  <Stack alignItems="flex-start" spacing={0.25}>
                    <Typography sx={{ fontWeight: 900 }}>Accepter + retour pièces</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Rachat validé + retour des pièces non conformes.
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 700 }}>
                      Net (moins livraison): {money(acceptedWithReturnTotal)} €
                    </Typography>
                  </Stack>
                </Button>

                {shippingAmount > 0 && (
                  <Alert icon={false} sx={{ bgcolor: BLUE_BG }}>
                    Livraison prise en compte: {money(shippingAmount)} €
                  </Alert>
                )}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button
            onClick={() => { setRejectWarningOpen(false); setDecisionDialogOpen(false); }}
            disabled={decisionLoading}
            sx={{ textTransform: 'none' }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔴 Confirmation refus (admin) */}
      <Dialog
        open={rejectWarningOpen}
        onClose={decisionLoading ? undefined : () => setRejectWarningOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: BLUE }}>
          Confirmer le refus
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography sx={{ color: 'text.secondary' }}>
            Attention : en refusant l’offre, la transaction basculera en refusée. Confirmer ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button
            onClick={() => setRejectWarningOpen(false)}
            disabled={decisionLoading}
            sx={{ textTransform: 'none' }}
          >
            Retour
          </Button>
          <Button
            variant="contained"
            onClick={() => submitDecision('rejected')}
            disabled={decisionLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              bgcolor: BLUE,
              '&:hover': { bgcolor: '#004890' },
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={wbSetupOpen} onClose={closeWaybillSetup} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>
          {t('admin.transactions.setup.title')}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
              minHeight: '70vh'
            }}
          >
            {/* PDF à gauche */}
            <Box sx={{ bgcolor: '#f6fbf7', p: 0 }}>
              {wbSetupErr ? (
                <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
                  <Alert severity="error">{wbSetupErr}</Alert>
                </Stack>
              ) : wbSetupBlobUrl ? (
                <iframe title="setup-waybill" src={wbSetupBlobUrl} style={{ width: '100%', height: '100%', border: 0 }} />
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 3 }}>
                  <CircularProgress size={18} />
                  <Typography>{t('admin.transactions.pdf.waybill.loading')}</Typography>
                </Stack>
              )}
            </Box>

            {/* Formulaire à droite */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={1.25}>
                <Typography sx={{ fontWeight: 800 }}>{t('admin.transactions.setup.refTitle')}</Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('admin.transactions.setup.help')}
                </Typography>

                {trackingInputs.map((value, idx) => (
                  <TextField
                    key={idx}
                    label={
                      idx === 0
                        ? t('admin.transactions.setup.inputLabel')
                        : `Numéro de colis ${idx + 1}`
                    }
                    placeholder={t('admin.transactions.setup.inputPlaceholder')}
                    value={value}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTrackingInputs((prev) => {
                        const next = [...prev];
                        next[idx] = val;
                        return next;
                      });
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalShippingIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                ))}

                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setTrackingInputs((prev) => [...prev, ''])
                  }
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Ajouter un n° de colis
                </Button>

                {/* 💰 montant de la livraison */}
                <TextField
                  label="Montant de la livraison (€)"
                  placeholder="Ex : 5.90"
                  value={shippingAmountInput}
                  onChange={e => setShippingAmountInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel id="shipping-method-label">Modèle d’e-mail d’expédition</InputLabel>
                  <Select labelId="shipping-method-label" label="Modèle d’e-mail d’expédition" value={shippingMethod} onChange={(e) => setShippingMethod(e.target.value)}>
                    <MenuItem value="pickup">E-mail avec enlèvement</MenuItem>
                    <MenuItem value="classic">E-mail bordereau classique</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Checkbox checked={autoSendShippingEmail} onChange={(e) => setAutoSendShippingEmail(e.target.checked)} />}
                  label="Envoyer automatiquement l’e-mail avec le numéro de suivi"
                />

                <Alert icon={false} sx={{ bgcolor: BLUE_BG }}>
                  {t('admin.transactions.setup.alert')}
                </Alert>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button onClick={closeWaybillSetup}>{t('admin.transactions.common.later')}</Button>
                  <Button
                    variant="contained"
                    onClick={saveTrackingNumber}
                    disabled={!hasAtLeastOneTracking || trackingSaving}
                    sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
                  >
                    {trackingSaving ? t('admin.transactions.setup.saving') : t('admin.transactions.common.save')}
                  </Button>

                  {/* 💌 Préparer l’e-mail (bordereau) */}
                  <Button
                    variant="outlined"
                    startIcon={<MailOutlineIcon />}
                    onClick={() => openMail(wbSetupFor, { lang: 'fr', scenario: 'waybill' })}
                    disabled={!wbSetupFor}
                    sx={{ borderColor: BORDER, '&:hover': { borderColor: BLUE } }}
                  >
                    {t('admin.transactions.actions.sendWaybillEmail') || 'Envoyer bordereau'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      
      {/* ✉️ Choix du type d'e-mail */}
      <Dialog
        open={mailTypeOpen}
        onClose={closeMailTypeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: BLUE,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MailOutlineIcon /> Choisir un modèle d'e-mail
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sélectionnez le type de message à envoyer pour le dossier {mailTypeTx?.numPackage || ''}.
            </Typography>

            <Button
              variant="contained"
              size="small"
              startIcon={<LocalShippingIcon />}
              onClick={() => handleMailScenarioSelect('waybill')}
              disabled={!mailTypeTx?.statusWaybillCreated}
              sx={{ justifyContent: 'flex-start', bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
            >
              Bordereau d’expédition
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<MoveToInboxIcon />}
              onClick={() => handleMailScenarioSelect('reception')}
              disabled={!mailTypeTx?.statusReception}
              sx={{ justifyContent: 'flex-start', borderColor: BORDER, '&:hover': { borderColor: BLUE } }}
            >
              Confirmation de réception
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => handleMailScenarioSelect('control')}
              disabled={!mailTypeTx?.statusPdfRecap}
              sx={{ justifyContent: 'flex-start', borderColor: BORDER, '&:hover': { borderColor: BLUE } }}
            >
              Récapitulatif de contrôle
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AccountBalanceIcon />}
              onClick={() => handleMailScenarioSelect('payment')}
              disabled={!mailTypeTx?.statusPayment}
              sx={{ justifyContent: 'flex-start', borderColor: BORDER, '&:hover': { borderColor: BLUE } }}
            >
              Confirmation de paiement
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MailOutlineIcon />}
              onClick={() => {
                if (!mailTypeTx) return;
                closeMailTypeDialog();
                openManualMail(mailTypeTx, { lang: 'fr' });
              }}
              sx={{ justifyContent: 'flex-start', borderColor: BORDER, '&:hover': { borderColor: BLUE } }}
            >
              Écrire un e-mail
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMailTypeDialog}>{t('admin.transactions.common.close')}</Button>
        </DialogActions>
      </Dialog>
      {/* ✉️ Modal e-mail multilingue */}
      <Dialog open={mailOpen} onClose={closeMail} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE, display:'flex', alignItems:'center', gap:1 }}>
          <MailOutlineIcon /> {t('admin.transactions.mail.title') || 'Nouveau message'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            {/* Langue + bouton réinjecter le modèle */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="lang-label"><TranslateIcon sx={{ mr: 0.5 }} /> Langue</InputLabel>
                <Select
                  labelId="lang-label"
                  label="Langue"
                  value={mailLang}
                  onChange={(e)=>{
                    const lang = e.target.value;
                    setMailLang(lang);
                    applyTemplate(mailScenario, lang, mailFor, { forceReplace: false });
                  }}
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="pl">Polski</MenuItem>
                  <MenuItem value="pt">Português</MenuItem>
                  <MenuItem value="cs">Čeština</MenuItem>
                  <MenuItem value="nl">Nederlands</MenuItem>
                </Select>
                <FormHelperText sx={{ display: 'none' }}> </FormHelperText>
              </FormControl>
              <Tooltip title="Réinsérer le modèle (remplace le sujet et le message)">
                <span>
                  <Button
                    size="small"
                    startIcon={<RestoreIcon />}
                    onClick={() => {
                      applyTemplate(mailScenario, mailLang, mailFor, { forceReplace: true });
                    }}
                    disabled={!mailFor}
                  >
                    Insérer le modèle
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                label={t('admin.transactions.mail.to') || 'Destinataire'}
                value={mailTo}
                onChange={(e)=>setMailTo(e.target.value)}
              />
              <Tooltip title={t('admin.transactions.actions.copyEmail') || 'Copier e-mail'}>
                <span>
                  <IconButton onClick={()=>copyEmail(mailTo)} disabled={!mailTo}>
                    <ContentCopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <TextField
              fullWidth
              size="small"
              label={t('admin.transactions.mail.subject') || 'Sujet'}
              value={mailSubject}
              onChange={(e)=>setMailSubject(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={8}
              label={t('admin.transactions.mail.body') || 'Message'}
              value={mailBody}
              onChange={(e)=>setMailBody(e.target.value)}
            />
            {/* Cases PJ avec disable si fichiers absents */}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControlLabel
                control={
                  <Tooltip title={mailFor?.statusWaybillCreated ? '' : "Bordereau indisponible pour cette transaction"}>
                    <span>
                      <Checkbox
                        checked={mailAttachWaybill}
                        onChange={(_,v)=> {
                          setMailAttachWaybill(v);
                        }}
                        disabled={!mailFor?.statusWaybillCreated}
                      />
                    </span>
                  </Tooltip>
                }
                label={t('admin.transactions.mail.attachWaybill') || 'Joindre bordereau (PDF)'}
              />
              <FormControlLabel
                control={
                  <Tooltip title={mailFor?.statusPdfRecap ? '' : "Récapitulatif indisponible pour cette transaction"}>
                    <span>
                      <Checkbox
                        checked={mailAttachRecap}
                        onChange={(_,v)=> {
                          setMailAttachRecap(v);
                        }}
                        disabled={!mailFor?.statusPdfRecap}
                      />
                    </span>
                  </Tooltip>
                }
                label={t('admin.transactions.mail.attachRecap') || 'Joindre récap (PDF)'}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('admin.transactions.mail.note')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMail}>{t('admin.transactions.common.close')}</Button>
          <Button
            variant="contained"
            startIcon={<MailOutlineIcon />}
            onClick={sendManualEmail}
            disabled={mailSending || !mailTo || !mailSubject}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
          >
            {mailSending ? (t('admin.transactions.mail.sending') || 'Envoi…') : (t('admin.transactions.mail.send') || 'Envoyer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal fiche contact utilisateur */}
      <Dialog
        open={!!contactUser}
        onClose={closeContactUser}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: BLUE,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Avatar
            sx={{
              bgcolor: BLUE,
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {contactUser
              ? (contactUser.firstname?.[0] ||
                 contactUser.name?.[0] ||
                 contactUser.email?.[0] ||
                 '?').toUpperCase()
              : '?'}
          </Avatar>
          Fiche contact
        </DialogTitle>
        <DialogContent dividers>
          {contactUser && (
            <Stack spacing={2}>
              {/* Email principal */}
              <Stack direction="row" spacing={1} alignItems="center">
                <MailOutlineIcon fontSize="small" />
                <Typography variant="body2">
                  <b>Email :</b> {contactUser.email || '—'}
                </Typography>
              </Stack>

              {/* Profil d'expédition complet */}
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1 }}
                >
                  Profil d’expédition
                </Typography>

                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BusinessIcon fontSize="small" />
                    <Typography variant="body2">
                      <b>Entreprise :</b> {contactUser.companyName || '—'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2">
                      <b>Nom / Prénom :</b>{' '}
                      {[contactUser.firstname, contactUser.name]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </Typography>
                  </Stack>

                  <Typography variant="body2">
                    <b>Téléphone :</b> {contactUser.phone || '—'}
                  </Typography>

                  <Typography variant="body2">
                    <b>Adresse :</b>{' '}
                    {[
                      contactUser.address,
                      [contactUser.postalCode, contactUser.city]
                        .filter(Boolean)
                        .join(' '),
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeContactUser}>{t('admin.transactions.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Modal IBAN admin */}
      <Dialog open={ibanOpen} onClose={closeIban} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>{t('admin.transactions.iban.title')}</DialogTitle>
        <DialogContent dividers>
          {ibanLoading ? (
            <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18}/> {t('admin.transactions.loading')}</Stack>
          ) : ibanErr ? (
            <Alert severity="error">{ibanErr}</Alert>
          ) : ibanData ? (
            <Stack spacing={1.25}>
              <Typography>{t('admin.transactions.iban.masked')} <b>{ibanData.ibanMasked || '—'}</b></Typography>
              <Typography>{t('admin.transactions.iban.holder')} <b>{ibanData.ownerName || '—'}</b></Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('admin.transactions.iban.caption')}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LockOpenIcon />}
                  disabled={revealBusy}
                  onClick={revealIban}
                  sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
                >
                  {t('admin.transactions.iban.reveal')}
                </Button>
                {revealBusy && <CircularProgress size={16} />}
              </Stack>

              {revealPlain?.iban && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <b>IBAN:</b> {revealPlain.iban}
                  </Typography>
                  {revealPlain.ownerName && (
                    <Typography variant="body2">
                      <b>{t('admin.transactions.iban.holder').replace(':','')}:</b> {revealPlain.ownerName}
                    </Typography>
                  )}
                </Alert>
              )}
            </Stack>
          ) : (
            <Typography sx={{ color: 'text.secondary' }}>{t('admin.transactions.iban.noData')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIban}>{t('admin.transactions.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog résultat Révélation */}
      <Dialog open={ibanDialogOpen} onClose={()=>setIbanDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>{t('admin.transactions.reveal.title')}</DialogTitle>
        <DialogContent dividers>
          {revealPlain?.iban ? (
            <Stack spacing={1}>
              <Typography sx={{ wordBreak: 'break-all' }}><b>IBAN :</b> {revealPlain.iban}</Typography>
              {revealPlain.ownerName ? <Typography><b>{t('admin.transactions.iban.holder').replace(':','')}:</b> {revealPlain.ownerName}</Typography> : null}
              <Alert severity="warning">{t('admin.transactions.reveal.warning')}</Alert>
            </Stack>
          ) : (
            <Typography>{t('admin.transactions.common.unavailable')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={()=>{ if (revealPlain?.iban) navigator.clipboard?.writeText(revealPlain.iban); }}
            disabled={!revealPlain?.iban}
            sx={{ color: BLUE }}
          >
            {t('admin.transactions.reveal.copy')}
          </Button>
          <Button onClick={()=>setIbanDialogOpen(false)}>{t('admin.transactions.common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* ✨ Modal retour colis après refus */}
      {/* ✨ Modal retour colis après refus */}
      <Dialog open={returnOpen} onClose={closeReturnDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: BLUE }}>
          Retour colis — offre refusée
        </DialogTitle>
        <DialogContent dividers>
          {/* ... contenu ... */}
        </DialogContent>
        <DialogActions>
          {/* ... tes boutons existants (Annuler / Enregistrer) ... */}
        </DialogActions>
      </Dialog>

      {/* ✨ Modal annulation transaction */}
      <Dialog
        open={cancelOpen}
        onClose={closeCancelDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#ef6c00' }}>
          Annuler la transaction
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography>
              Vous pouvez annuler cette transaction. Elle sera affichée en orange et les actions seront désactivées.
            </Typography>
            <TextField
              label="Motif de l’annulation (optionnel)"
              value={cancelMsg}
              onChange={(e) => setCancelMsg(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog}>
            {t('admin.transactions.common.close')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={saveCancel}
            disabled={cancelSaving}
            sx={{ bgcolor: '#ef6c00', '&:hover': { bgcolor: '#d84315' } }}
          >
            {cancelSaving ? 'Annulation…' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
